import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import moment from "moment";
import { DateRangePicker, DateRangeValue } from "../../../components/ui/daterange-picker";
import {
  checkInDateSelected,
  checkInDateSelectedClear,
  checkOutDateSelected,
  checkOutDateSelectedClear,
  startTimeSelected,
  endTimeSelected,
  eventTypeSelected,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";
import NumericInput from "../../common/NumberOfCustomers";
import CityAndLocationSelection from "../common/locationSelection/CityAndDistrictSelection";
import {useGetMaxGuestsQuery} from "../../../api/locations/locationsApiSlice";
import React, {useMemo} from "react";
import { isOutsideBookableDays, useBookingWindow } from "../common/hooks/useBookingWindow";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import BookingTimeSelect from "../common/components/OrderDataStep/BookingTimeSelect";
import {getAvailableBookingTimes} from "../../../util";
import {useEventTypeOptions} from "../common/hooks/useEventTypeOptions";
import {
  resolveCategoryNote,
  resolveEventTypeInput,
  useActiveProductCategory,
} from "../../../util/categoryConfig";
import { Input } from "../../../components/ui/input";
import { EVENT_TYPE_MAX_LENGTH } from "../common/url/bookingQuerySchema";
import { getLocalizedText } from "../../../util/localizedText";
import { useObjectFirstEntry } from "../common/hooks/useObjectFirstEntry";
import { useHeadCount } from "../common/hooks/useHeadCount";
import PickedRoomSummary from "../common/components/ObjectFirst/PickedRoomSummary";
import { usePickedRoomTimeZone } from "../../../util/locationTimeZone";
import { useEventFlowUseCase } from "./eventFlowUseCase";

/**
 * Strips the characters the booking URL refuses (`EVENT_TYPE_PATTERN`: no
 * `\p{C}`) from a typed event name. A name holding one — the zero-width joiner
 * inside a family emoji, a soft hyphen pasted from Word — fails the URL
 * round-trip, reads back as "no event" and the whole field silently clears.
 * Dropped here, the rest of what the customer typed stays.
 */
export const withoutControlCharacters = (value: string): string => value.replace(/\p{C}/gu, "");

const LocationAndDurationSelectionStep = () => {
  // The event-flow use case this screen books (venue, or a sports hall).
  const useCase = useEventFlowUseCase();
  const { t, i18n } = useTranslation();
  const locations = useSelector(
      (state: RootState) => state.booking.useCaseLocations
  );

  const { data: guestsMax } = useGetMaxGuestsQuery({ useCase }, {
    refetchOnMountOrArgChange: 60
  });

  // The tenant's own vocabulary where it configured one, the standard list
  // otherwise — see useEventTypeOptions.
  const eventTypeOptions = useEventTypeOptions(useCase);

  // The venue category's own note, or nothing (#2889). This line used to be a
  // translation string shown to EVERY tenant — one tenant's weekend handover
  // rule, untrue for everyone else. Now each category says its own, and a
  // category that says nothing shows no line at all.
  const categoryKey = useActiveProductCategory();
  const categoryNote = useSelector((state: RootState) =>
    resolveCategoryNote(state.tenantConfig?.config, useCase, categoryKey)
  );
  const noteText = getLocalizedText(categoryNote, i18n.language).trim();
  // Dropdown of event types, or a free-text name for the event — a
  // per-category choice (`eventTypeInput`), dropdown by default.
  const eventTypeInput = useSelector((state: RootState) =>
    resolveEventTypeInput(state.tenantConfig?.config, useCase, categoryKey)
  );

  // #3035: came in object-first? Then this step is the time for ONE room.
  const { pickedRoom } = useObjectFirstEntry(useCase);
  // The clock the times are offered on (#3219): the picked room's location,
  // else the booking flow's (the search area's).
  const timeZone = usePickedRoomTimeZone(pickedRoom?.locationId);

  const eventType = useSelector((state: RootState) => state.bookingCreation.orderData.eventType);
  const startTime = useSelector((state: RootState) => state.bookingCreation.orderData.startTime || "0");
  const endTime = useSelector((state: RootState) => state.bookingCreation.orderData.endTime || "0");

  const startDate = useSelector(
      (state: RootState) => state.bookingCreation.orderData.checkInDate
  );
  const endDate = useSelector(
      (state: RootState) => state.bookingCreation.orderData.checkOutDate
  );

  const dispatch = useDispatch();

  // The booking window this tenant configured for venues — the lead buffer and
  // the horizon. It replaces `moment().add(5, "days")` and
  // `moment().add(18, "months")`, which were hardcoded here for one tenant and
  // then applied to every tenant on this use case. Those numbers are now the
  // DEFAULTS, so an unconfigured tenant behaves exactly as it did before.
  //
  // ADR-0020: this greys out the dates the app API refuses — the rule itself
  // lives in `backend/src/core/lib/bookingWindow.ts`. The buffers apply to
  // CUSTOMER bookings only; staff creating a booking in the admin area are
  // deliberately not subject to them (PO, 2026-09-20).
  const bookingWindow = useBookingWindow(useCase, timeZone);
  const minDate = bookingWindow.earliest;

  // Calendar-day labels, not a phone midnight against Berlin instants.
  const isDateDisabled = (d: Date) =>
    isOutsideBookableDays(moment(d), minDate, bookingWindow.latest);

  const handleRangeChange = (range: DateRangeValue | null) => {
    if (!range || !range.from) {
      dispatch(checkInDateSelectedClear());
      dispatch(checkOutDateSelectedClear());
      return;
    }
    const fromMoment = moment(range.from).startOf("day");
    dispatch(checkInDateSelected({ checkInDate: fromMoment }));
    if (range.to) {
      // Venue allows same-day events: keep `to` even when equal to `from`.
      dispatch(
        checkOutDateSelected({ checkOutDate: moment(range.to).startOf("day") })
      );
    } else {
      // Single date picked (no range end) → default check-out to the chosen
      // day so a same-day venue booking is valid and the start/end-time
      // selects (gated on both dates) enable. The user can still extend this
      // into a multi-day range; the picker treats {X, X} as an editable range.
      dispatch(checkOutDateSelected({ checkOutDate: fromMoment.clone() }));
    }
  };

  const dateRangeValue: DateRangeValue = useMemo(
    () => ({
      from: startDate ? startDate.toDate() : null,
      to: endDate ? endDate.toDate() : null,
    }),
    [startDate, endDate]
  );

  const roomRentalLocation = useSelector(
      (state: RootState) => state.bookingCreation.roomRentalLocation
  )

  const currentGuestsMax: number = useMemo(() => {
    if (guestsMax && roomRentalLocation?.city) {
      return guestsMax[roomRentalLocation.city] || 100;
    }
    return 100
  }, [roomRentalLocation, guestsMax])



  const handleEventTypeChange = (value: string) => {
    // eventTypeSelected now recomputes nextEnabled against all venue
    // fields (city, district, dates, times, eventType, guests), so the
    // previously-redundant `setNextEnabled(!!value)` is removed.
    dispatch(eventTypeSelected({ eventType: value }));
  };

  const headCount = useHeadCount(useCase, currentGuestsMax, pickedRoom);

  // Time slots are chosen before the venue is selected, so we union the
  // opening hours across all locations for this use case (lowest fromHour,
  // highest toHour per weekday). Object-first (#3035) knows the venue
  // already, so only its own location's hours apply — unless that location is
  // not among the loaded ones, where the union is still the honest answer.
  const allOpeningHours = useMemo(() => {
    const all = locations ?? [];
    const ownLocationId = pickedRoom?.locationId;
    const own = ownLocationId ? all.filter((loc) => loc.id === ownLocationId) : [];
    return (own.length > 0 ? own : all).flatMap((loc) => loc.openingHours ?? []);
  }, [locations, pickedRoom]);

  let availableStartTimes: string[] = [];
  let availableEndTimes: string[] = [];
  if (startDate && endDate) {
    try {
      const { availableStartTimes: startTimes, availableEndTimes: endTimes } = getAvailableBookingTimes(
        startDate,
        endDate,
        startTime,
        endTime,
        allOpeningHours,
        Date.now(),
        timeZone,
      );
      availableStartTimes = startTimes;
      availableEndTimes = endTimes;
    } catch (e) {
      // Invalid date range, keep arrays empty
    }
  }

  const handleStartTimeChange = (value: string) => {
    dispatch(startTimeSelected({ startTime: value }));
  };

  const handleEndTimeChange = (value: string) => {
    dispatch(endTimeSelected({ endTime: value }));
  };

  // Where the booking is: the picked room when the customer came in
  // object-first (#3035), the city/district choice otherwise.
  const renderWhere = () => {
    if (pickedRoom?.city) return <PickedRoomSummary room={pickedRoom} />;
    if (locations) return <CityAndLocationSelection locations={locations} />;
    return <p className="text-foreground/80">{t("book_location.no_location")}</p>;
  };

  return (
      <div className="flex flex-col w-full max-w-2xl gap-6 px-3 pb-12 mx-auto sm:px-5">
        <div className="w-full">
          {renderWhere()}
        </div>

        <div className="w-full flex flex-col gap-2">
          <label className="text-sm font-medium text-foreground">
            {t("apartment.check_in")} – {t("apartment.check_out")}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <DateRangePicker
              value={dateRangeValue}
              onChange={handleRangeChange}
              isDateDisabled={isDateDisabled}
              defaultMonth={minDate.toDate()}
              buttonClassName="w-full h-[62px] text-base border rounded-[12px] bg-white text-foreground border-primary hover:border-primary"
              aria-label={`${t("apartment.check_in")} – ${t("apartment.check_out")}`}
              clearable
          />
          <p className="text-xs text-foreground">
            {t("book_location.earliest_available_date", {
              date: minDate.format("LL"),
            })}
          </p>
        </div>

        <div className="w-full flex items-center justify-between">
          <div className="w-full">
            <BookingTimeSelect
                availableSlots={availableStartTimes}
                value={startTime}
                onChange={handleStartTimeChange}
                availableDurations={[]}
                disabled={!startDate || !endDate}
                formControlClassName={"w-full"}
                label={t("general.start_time")}
            />
          </div>
          <div className="flex items-center justify-center text-foreground/40 font-bold mx-4">
            -
          </div>
          <div className="w-full">
            <BookingTimeSelect
                availableSlots={availableEndTimes}
                value={endTime}
                onChange={handleEndTimeChange}
                availableDurations={[]}
                disabled={!startDate || !endDate}
                formControlClassName={"w-full"}
                label={t("general.end_time")}
            />
          </div>
        </div>

        {noteText && (
          <p className="text-xs text-foreground whitespace-pre-line">{noteText}</p>
        )}

        <div className="flex items-center justify-between gap-x-2 sm:gap-x-4">
          <span className="text-base font-normal text-foreground/70">
            {t(headCount.labelKey)}
          </span>
          <NumericInput max={headCount.max} labelKey={headCount.labelKey} />
        </div>
        {eventTypeInput === "freeText" ? (
        <div className="flex flex-col gap-2 mt-1 mb-2">
          <label htmlFor="venue-event-title" className="text-sm font-medium text-foreground">
            {t("venue_reservation.event_title")}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <Input
            id="venue-event-title"
            value={eventType ?? ""}
            // The URL carries the name (`EVENT_TYPE_PATTERN`, 40 characters);
            // a longer one would not survive a reload.
            maxLength={EVENT_TYPE_MAX_LENGTH}
            placeholder={t("venue_reservation.event_title_placeholder")}
            onChange={(e) => handleEventTypeChange(withoutControlCharacters(e.target.value))}
            onBlur={(e) => handleEventTypeChange(withoutControlCharacters(e.target.value).trim())}
            className="w-full h-[62px] text-base border rounded-[12px] bg-white text-foreground border-primary"
          />
        </div>
        ) : (
        <div className="flex flex-col gap-2 mt-1 mb-2">
          <label className="text-sm font-medium text-foreground">
            {t("venue_reservation.event_type")}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <Select
            value={eventType ?? undefined}
            onValueChange={handleEventTypeChange}
          >
            <SelectTrigger
              className="w-full h-[62px] text-base border rounded-[12px] bg-white text-foreground border-primary hover:border-primary"
              aria-label={t("venue_reservation.event_type")}
            >
              <SelectValue placeholder={t("venue_reservation.event_type")} />
            </SelectTrigger>
            <SelectContent>
              {eventTypeOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        )}
      </div>
  );
};

export default LocationAndDurationSelectionStep;
